import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const isIOS = () => typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);

/**
 * Waveform – ekte lydnivå fra mikrofonen (Web Audio AnalyserNode → transform på stolpene, ingen re-render per frame).
 * På iOS deler vi ikke mikrofonen med talegjenkjenningen (kan stoppe den) – der, og når tilgang mangler, kjører en
 * rolig CSS-animasjon i stedet så brukeren alltid ser at det «lytter».
 */
export function Waveform({ active, bars = 28, className }) {
  const ref = useRef(null);
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    if (!active) { setFallback(false); return undefined; }
    if (isIOS() || !navigator.mediaDevices?.getUserMedia) { setFallback(true); return undefined; }
    let alive = true;
    let raf = 0;
    let ctx;
    let stream;
    const data = new Uint8Array(64);
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        const AC = window.AudioContext || window.webkitAudioContext;
        ctx = new AC();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.72;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const tick = () => {
          if (!alive) return;
          analyser.getByteFrequencyData(data);
          const els = ref.current?.children || [];
          const n = els.length;
          for (let i = 0; i < n; i += 1) {
            const d = Math.abs(i - (n - 1) / 2) / ((n - 1) / 2); // 0 i midten → 1 i kanten
            const bin = Math.min(data.length - 1, 2 + Math.floor((1 - d) * 18));
            const v = data[bin] / 255;
            els[i].style.transform = `scaleY(${(0.12 + v * (0.95 - d * 0.55)).toFixed(3)})`;
          }
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        if (alive) setFallback(true);
      }
    })();
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close?.().catch?.(() => {});
      const els = ref.current?.children || [];
      for (const el of els) el.style.transform = "";
    };
  }, [active]);
  return (
    <div ref={ref} className={cn("voice-wave", className)} data-active={active ? "true" : "false"} data-fallback={fallback ? "true" : "false"} aria-hidden>
      {Array.from({ length: bars }, (_, i) => <i key={i} style={{ "--i": i, "--h": (0.35 + Math.abs(Math.sin(i * 1.7)) * 0.6).toFixed(2), "--wd": `${620 + ((i * 97) % 380)}ms`, "--wdelay": `${-((i * 131) % 700)}ms` }} />)}
    </div>
  );
}
